import React from 'react';
import { Status } from '@/components/Status';
import { FilePreviewCard } from '@components/FilePreviewCard';
import { Vehicle } from '@/services/api/vehicles';
import { VehicleStatus, getBusinessUnitLabel } from '@/types/common';
import VehicleTelemetrySection from './VehicleTelemetrySection';

interface VehicleDetailContentProps {
  vehicle: Vehicle;
}

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

const statusLabelMapper: Record<VehicleStatus, string> = {
  [VehicleStatus.IDLE]: 'Idle',
  [VehicleStatus.ACTIVE]: 'Active',
  [VehicleStatus.LOST]: 'Lost',
  [VehicleStatus.IN_REPAIR]: 'In Repair',
  [VehicleStatus.BROKEN]: 'Broken',
  [VehicleStatus.RETURNED]: 'Returned',
};

const VehicleDetailContent: React.FC<VehicleDetailContentProps> = ({
  vehicle,
}) => {
  const stnkFileUrl = vehicle.stnkAnnualFileUrl || vehicle.stnkFiveYearFileUrl;
  const stnkFileName = stnkFileUrl ? stnkFileUrl.split('/').pop() : null;

  const handleOpenStnkFile = () => {
    if (!stnkFileUrl) return;
    // Stored value is a directly-accessible URL (files live in the main
    // bucket now — no signed-URL round-trip needed).
    window.open(stnkFileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="w-full space-y-6 lg:flex-1">
      <div className="border border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <div className="px-5 pt-4 pb-2">
          <div className="text-[16px] font-medium text-gray-800">
            Vehicle Details
          </div>
        </div>

        <div className="bg-gray-50 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Detail
        </div>

        <div className="divide-y divide-gray-100">
          {/* Plate Number / Status */}
          <div className="grid grid-cols-2 gap-4 px-5 py-3">
            <div>
              <div className="text-xs text-gray-400 mb-1">Plate Number</div>
              <div className="text-sm font-semibold text-gray-900">
                {displayValue(vehicle.platNomor)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">Status</div>
              <div>
                {vehicle.status ? (
                  <Status
                    value={
                      statusLabelMapper[vehicle.status as VehicleStatus] ||
                      vehicle.status
                    }
                    type="feedback"
                  />
                ) : (
                  <span className="text-sm font-semibold text-gray-900">-</span>
                )}
              </div>
            </div>
          </div>

          {/* OEM Name / Model Name */}
          <div className="grid grid-cols-2 gap-4 px-5 py-3">
            <div>
              <div className="text-xs text-gray-400 mb-1">OEM Name</div>
              <div className="text-sm font-semibold text-gray-900">
                {displayValue(vehicle.oemName)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">Model Name</div>
              <div className="text-sm font-semibold text-gray-900">
                {displayValue(vehicle.modelName)}
              </div>
            </div>
          </div>

          {/* Hub Location / Business Unit */}
          <div className="grid grid-cols-2 gap-4 px-5 py-3">
            <div>
              <div className="text-xs text-gray-400 mb-1">Hub Location</div>
              <div className="text-sm font-semibold text-gray-900">
                {displayValue(vehicle.hubName)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">Business Unit</div>
              <div className="text-sm font-semibold text-gray-900">
                {getBusinessUnitLabel(vehicle.businessUnit)}
              </div>
            </div>
          </div>

          {/* Nomor Rangka/VIN */}
          <div className="px-5 py-3">
            <div className="text-xs text-gray-400 mb-1">Nomor Rangka/VIN</div>
            <div className="text-sm font-semibold text-gray-900">
              {displayValue(vehicle.vin)}
            </div>
          </div>

          {/* Nomor Mesin */}
          <div className="px-5 py-3">
            <div className="text-xs text-gray-400 mb-1">Nomor Mesin</div>
            <div className="text-sm font-semibold text-gray-900">
              {displayValue(vehicle.imei)}
            </div>
          </div>

          {/* Contract Duration */}
          <div className="px-5 py-3">
            <div className="text-xs text-gray-400 mb-1">Contract Duration</div>
            <div className="text-sm font-semibold text-gray-900">
              {vehicle.contractDuration
                ? `${vehicle.contractDuration} Month`
                : '-'}
            </div>
          </div>

          {/* Contract Start Date / Contract End Date */}
          <div className="grid grid-cols-2 gap-4 px-5 py-3">
            <div>
              <div className="text-xs text-gray-400 mb-1">
                Contract Start Date
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {formatDateTime(vehicle.contractStartDate)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">
                Contract End Date
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {formatDateTime(vehicle.contractEndDate)}
              </div>
            </div>
          </div>

          {/* Color */}
          <div className="px-5 py-3">
            <div className="text-xs text-gray-400 mb-1">Color</div>
            <div className="text-sm font-semibold text-gray-900">
              {displayValue(vehicle.color)}
            </div>
          </div>

          {/* STNK Link */}
          <div className="px-5 py-3">
            <div className="text-xs text-gray-400 mb-1">STNK Link</div>
            <div className="text-sm font-semibold text-gray-900 break-all">
              {vehicle.stnkLink ? (
                <a
                  href={vehicle.stnkLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#5E2AAC] hover:underline"
                >
                  {vehicle.stnkLink}
                </a>
              ) : (
                '-'
              )}
            </div>
          </div>

          {/* App Username / App Password */}
          <div className="grid grid-cols-2 gap-4 px-5 py-3">
            <div>
              <div className="text-xs text-gray-400 mb-1">App Username</div>
              <div className="text-sm font-semibold text-gray-900">
                {displayValue(vehicle.appUsername)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">App Password</div>
              <div className="text-sm font-semibold text-gray-900">
                {displayValue(vehicle.appPassword)}
              </div>
            </div>
          </div>
        </div>

        <VehicleTelemetrySection vehicle={vehicle} />
      </div>

      <div className="border border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <div className="px-5 pt-4 pb-2">
          <div className="text-[16px] font-medium text-gray-800">
            Document Details
          </div>
        </div>

        <div className="bg-gray-50 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          STNK
        </div>

        <div className="divide-y divide-gray-100">
          {/* STNK PDF */}
          <div className="px-5 py-3">
            <div className="text-xs text-gray-400 mb-1">STNK (PDF)</div>
            {stnkFileName ? (
              <button onClick={handleOpenStnkFile} className="w-full text-left">
                <FilePreviewCard fileName={stnkFileName} showStatus={false} />
              </button>
            ) : (
              <div className="text-sm font-semibold text-gray-900">-</div>
            )}
          </div>

          {/* Masa Berlaku Tahunan / Masa Berlaku 5 Tahunan */}
          <div className="grid grid-cols-2 gap-4 px-5 py-3">
            <div>
              <div className="text-xs text-gray-400 mb-1">
                Masa Berlaku Tahunan
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {formatDateTime(vehicle.stnkAnnualExpiryDate ?? undefined)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">
                Masa Berlaku 5 Tahunan
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {formatDateTime(vehicle.stnkFiveYearExpiryDate ?? undefined)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailContent;
